import styles from './form.less';
import Checker from '../../../../../components/checker';
import {ImageOptions, ProductProps} from '../reviewModal';
import StarRating from '../../../../../components/starRating';
import SizeRating, {SizeRatingSize} from './form/sizeRating';

export {styles};

enum ErrorType {
    STARS = 1,
    TEXT = 2,
}

export interface FormSubmitOptions {
    stars: number;
    size?: number;
    text: string;
    image?: string;
    anonymous: boolean;
}

interface FormProps {
    loading: boolean;
    product: ProductProps;
    nickname: string;
    sizeRating: boolean;
    sizes: SizeRatingSize[];
    imageOptions: ImageOptions;
    onSubmit: (options: FormSubmitOptions) => void;
}

interface FormState {
    stars: number;
    size?: number;
    image?: string;
    imageThumbnail?: {
        src: string;
        name: string;
        size: number;
    };
    anonymous: boolean;
    error?: number;
}

export default class Form extends React.Component<FormProps, FormState> {

    private _bodyRef = React.createRef<HTMLDivElement>();
    private _textareaRef = React.createRef<HTMLTextAreaElement>();
    private _imagesWrapperRef = React.createRef<HTMLLabelElement>();
    private _imageInputRef = React.createRef<HTMLInputElement>();

    constructor(props) {
        super(props);

        this.state = {
            stars: 0,
            anonymous: false,
        };

        this.onStarsRatingChange = this.onStarsRatingChange.bind(this);
        this.onSizeRatingChange = this.onSizeRatingChange.bind(this);
        this.onAnonymousChange = this.onAnonymousChange.bind(this);
        this.onImageInputChange = this.onImageInputChange.bind(this);
        this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
    }

    onStarsRatingChange(stars: number) {
        this.setState({...this.state,
            stars: stars,
            error: this.state.error & ~ErrorType.STARS,
        });
    }

    onSizeRatingChange(size: number) {
        this.setState({...this.state,
            size: size,
        });
    }

    onAnonymousChange(checked: boolean) {
        this.setState({...this.state,
            anonymous: checked,
        });
    }

    onImageInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        let file = event.target.files?.item(0);
        if (file) {
            if (file.size > this.props.imageOptions.maxFileSize) {
                alert(_('max %s', Math.round(this.props.imageOptions.maxFileSize / 1024 / 1024) + ' MB'));
                return;
            }

            this._loadImageFile(file);
        } else {
            this.setState({...this.state,
                image: null,
            });
        }
    }

    onSubmitButtonClick() {
        if (this.props.loading) {
            return;
        }
        
        if (!this._validate()) {
            return;
        }

        this.props.onSubmit({
            stars: this.state.stars,
            size: this.state.size,
            text: this._textareaRef.current.value,
            image: this.state.image,
            anonymous: this.state.anonymous,
        });
    }

    componentDidMount() {
        $j(this._bodyRef.current).on('dragover drop', (event) => {
            event.preventDefault();
        }).on('dragover dragenter', () => {
            $j(this._imagesWrapperRef.current).toggleClass(styles.dragover, true);
        }).on('dragleave dragend drop', () => {
            $j(this._imagesWrapperRef.current).toggleClass(styles.dragover, false);
        }).on('drop', (_event) => {
            let event: DragEvent = _event.originalEvent as DragEvent;
            if (typeof event.dataTransfer != 'undefined') {
                let file = event.dataTransfer.files.item(0);
                if (file) {
                    if (file.size > this.props.imageOptions.maxFileSize) {
                        alert(_('max %s', Math.round(this.props.imageOptions.maxFileSize / 1024 / 1024) + ' MB'));
                        return;
                    }

                    this._loadImageFile(file);
                }
            }
        });
    }

    private _validate() {
        let error = 0;

        if (this.state.stars == 0) {
            error |= ErrorType.STARS;
        }

        if (!((/\S/).test(this._textareaRef.current.value))) {
            error |= ErrorType.TEXT;
        }

        this.setState({...this.state,
            error: error,
        });

        return error == 0;
    }

    private _loadImageFile(file: File) {
        let reader = new FileReader();

        reader.onload = (progressEvent) => {
            let img = new Image();

            img.onload = () => {
                let image = this._resizeImage(img, this.props.imageOptions.maxWidth, this.props.imageOptions.quality);

                image.onload = () => {
                    let imageThumbnail = this._resizeImage(image, 100, this.props.imageOptions.quality);

                    this.setState({...this.state,
                        image: image.src,
                        imageThumbnail: {
                            src: imageThumbnail.src,
                            name: file.name,
                            size: AitiCore.getUtils().round(file.size / 1024 / 1024, 1),
                        },
                    });

                    this._imageInputRef.current.value = null;
                }
            };

            img.src = progressEvent.target.result.toString();
        };

        reader.readAsDataURL(file);
    }

    private _resizeImage(image: HTMLImageElement, maxWidth: number, quality: number): HTMLImageElement {
        let canvas: HTMLCanvasElement = $j('<canvas style="display: none"/>').appendTo('body')[0] as HTMLCanvasElement;

        if (image.width > maxWidth) {
            canvas.width = maxWidth;
            canvas.height = canvas.width * (image.height / image.width);
        } else {
            canvas.width = image.width;
            canvas.height = image.height;
        }

        let ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

        let newImage = new Image();
        newImage.src = canvas.toDataURL('image/jpeg', quality / 100);

        canvas.remove();

        return newImage;
    }

    render() {
        const imageInputId = AitiCore.getUtils().getUniqueId('image');

        return (
            <>
                <div className={styles.body} ref={this._bodyRef}>
                    <div className={styles.product}>
                        <div className={styles.productImage}>
                            <img src={this.props.product.image}/>
                        </div>
                        <div className={styles.productName}>
                            {this.props.product.name}
                        </div>
                    </div>
                    <div className={styles.form}>
                        <div className={styles.row}>
                            <div className={styles.label}>{_('[REVIEW_MODAL] Overall rating')}</div>
                            <div className={classNames(styles.rating, {[styles.error]: this.state.error & ErrorType.STARS})}>
                                <div className={styles.ratingStars}>
                                    <StarRating
                                        stars={this.state.stars}
                                        onChange={this.onStarsRatingChange}
                                    />
                                </div>
                                <div className={styles.ratingText}>
                                    {Math.round((this.state.stars * 100) / 5) + '%'}
                                </div>
                            </div>
                        </div>

                        {this.props.sizeRating && (
                            <div className={styles.row}>
                                <div className={styles.label}>{_('[REVIEW_MODAL] Does the size fit?')}</div>
                                <div className={styles.sizeRating}>
                                    <SizeRating
                                        size={this.state.size}
                                        sizes={this.props.sizes}
                                        onChange={this.onSizeRatingChange}
                                    />
                                </div>
                            </div>
                        )}

                        <div className={styles.row}>
                            <div className={styles.label}>{_('[REVIEW_MODAL] Written review')}</div>
                            <textarea
                                ref={this._textareaRef}
                                className={classNames(styles.textarea, {[styles.error]: this.state.error & ErrorType.TEXT})}
                                placeholder={_('[REVIEW_MODAL] Describe your experience with the product')}
                                onInput={() => this.setState({...this.state, error: this.state.error & ~ErrorType.TEXT})}
                            />

                            {!this.state.image && (
                                <label ref={this._imagesWrapperRef} className={styles.imagesWrapper} htmlFor={imageInputId}>
                                    <div className={styles.imagesIcon}>
                                        <i className="fas fa-cloud-upload-alt"/>
                                    </div>
                                    <div className={styles.imagesText}>
                                        <div className={styles.imagesTextLarge}>{_('[REVIEW_MODAL] Upload a photo')}</div>
                                        <div className={styles.imagesTextSmall}>{_('max %s', Math.round(this.props.imageOptions.maxFileSize / 1024 / 1024) + ' MB')}</div>
                                    </div>
                                </label>
                            )}

                            {this.state.image && (
                                <div className={styles.image}>
                                    <img src={this.state.imageThumbnail.src}/>
                                    <div className={styles.imageInfo}>
                                        <div className={styles.imageName}>{this.state.imageThumbnail.name}</div>
                                        <div className={styles.imageSize}>{this.state.imageThumbnail.size} MB</div>
                                        <div className={styles.imageRemove} onClick={() => this.setState({...this.state, image: null, imageThumbnail: null})}>{_('Remove')}</div>
                                    </div>
                                </div>
                            )}

                            <input id={imageInputId} ref={this._imageInputRef} type="file" accept="image/*" style={{display: 'none'}} onChange={this.onImageInputChange}/>
                        </div>
                        <div className={styles.row}>
                            <div className={styles.label}>{_('[REVIEW_MODAL] Public name')}</div>
                            <div className={styles.nameWrapper}>
                                <div className={styles.nameText}>
                                    {this.state.anonymous ? _('Customer') : this.props.nickname}
                                </div>
                                <Checker
                                    label={_('[REVIEW_MODAL] Anonymous review')}
                                    onChange={this.onAnonymousChange}
                                    styles={{
                                        label: styles.nameCheckerLabel
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.bottom}>
                    <div className={styles.button} onClick={this.onSubmitButtonClick}>{_('[REVIEW_MODAL] Submit review')}</div>
                </div>
            </>
        )
    }
}
